import { motion } from "framer-motion";
import { Github, Linkedin, Mail, ArrowDown, Download } from "lucide-react";
import { personalInfo } from "../data/portfolio";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.23, 1, 0.32, 1] },
});

export default function Hero() {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-center px-6 md:px-16 lg:px-24 pt-20 pb-16"
    >
      {/* Badge */}
      <motion.div {...fadeUp(0.15)} className="mb-8">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono tracking-wide bg-blue-500/8 border border-blue-400/20 text-blue-400">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_8px_#34d399] animate-pulse" />
          {personalInfo.availableForWork ? "Open to opportunities" : "Currently unavailable"}
        </span>
      </motion.div>

      {/* Name */}
      <motion.h1
        {...fadeUp(0.28)}
        className="font-display font-black leading-none tracking-tight mb-6"
        style={{ fontSize: "clamp(3.5rem, 10vw, 7.5rem)" }}
      >
        <span className="block text-white">{personalInfo.name.split(" ")[0]}</span>
        <span className="block grad-text">{personalInfo.name.split(" ").slice(1).join(" ")}</span>
      </motion.h1>

      {/* Role */}
      <motion.p
        {...fadeUp(0.42)}
        className="font-mono text-sm md:text-base tracking-widest text-white/40 uppercase mb-4 max-w-xl"
      >
        {personalInfo.role}
      </motion.p>

      {/* Tagline */}
      <motion.p
        {...fadeUp(0.54)}
        className="text-xl md:text-2xl text-white/60 font-light max-w-xl leading-relaxed mb-10"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {personalInfo.tagline}
      </motion.p>

      {/* Divider line */}
      <motion.div
        {...fadeUp(0.62)}
        className="w-16 h-px mb-8"
        style={{ background: "linear-gradient(to right, #60a5fa, #a78bfa)" }}
      />

      {/* CTAs */}
      <motion.div {...fadeUp(0.7)} className="flex flex-wrap gap-4 mb-12">
        <a
          href="#projects"
          data-hover
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(96,165,250,0.3)]"
          style={{ background: "linear-gradient(135deg, #60a5fa, #a78bfa)" }}
        >
          View Projects
          <ArrowDown size={14} className="rotate-[-90deg]" />
        </a>
        <a
          href={personalInfo.resumeUrl}
          download
          data-hover
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm text-white/70 border border-white/10 hover:text-white hover:border-white/20 hover:bg-white/[0.04] transition-all duration-300 hover:-translate-y-1"
        >
          <Download size={14} />
          Download CV
        </a>
      </motion.div>

      {/* Social Links */}
      <motion.div {...fadeUp(0.82)} className="flex items-center gap-5">
        {[
          { Icon: Github, href: personalInfo.github, label: "GitHub" },
          { Icon: Linkedin, href: personalInfo.linkedin, label: "LinkedIn" },
          { Icon: Mail, href: `mailto:${personalInfo.email}`, label: "Email" },
        ].map(({ Icon, href, label }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noreferrer"
            data-hover
            aria-label={label}
            className="flex items-center justify-center w-10 h-10 rounded-lg border border-white/[0.08] text-white/40 hover:text-white hover:border-blue-400/30 hover:bg-white/[0.04] transition-all duration-300 hover:-translate-y-1"
          >
            <Icon size={17} />
          </a>
        ))}
        <div className="w-px h-5 bg-white/10 mx-1" />
        <span className="font-mono text-xs text-white/25 tracking-wide">{personalInfo.email}</span>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/25">scroll</span>
        <div
          className="w-px h-12 animate-[scrollLine_2s_ease-in-out_infinite]"
          style={{ background: "linear-gradient(to bottom, #60a5fa, transparent)" }}
        />
      </motion.div>
    </section>
  );
}
